import type { ANavigationProfileInterface } from '@atabix/vue-ui'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { LocaleService } from '@/helpers/LocaleService'
import type { User } from '@/models/User'

export const useAuthStore = defineStore(
  'auth',
  () => {
    const isInitalized = ref(false)
    const isLoading = ref(false)
    /** Determines whether the use has manually logged out at least once. */
    const hasLoggedOut = ref(false)
    const user = ref<User | null>(null)
    const proxyOfUser = ref<User | null>(null)
    const isSelectingProxy = ref(false)

    async function setUser(data?: User) {
      if (data && isSelectingProxy.value && data.id !== user.value?.id) {
        proxyOfUser.value = user.value || null
      }
      user.value = data || null

      if (user.value?.language) {
        LocaleService.setAppLocale(user.value.language)
      }

      if (!data) {
        isSelectingProxy.value = false
        proxyOfUser.value = null
      }
    }

    function getUser(): User | null {
      return user.value as User | null
    }

    function setLoading(value: boolean = true) {
      isLoading.value = value
    }

    function setInitalized(value: boolean = true) {
      isInitalized.value = value
    }

    function setIsSelectingProxy(value: boolean = true) {
      isSelectingProxy.value = value
    }

    function setHasLoggedOut(value: boolean = true) {
      hasLoggedOut.value = value
    }

    function clear() {
      setUser()
    }

    const hasUser = computed(() => !!user.value)

    const isAuthenticated = computed(() => !!user.value)

    return {
      user,
      proxyOfUser,
      clear,
      isAuthenticated,
      hasLoggedOut,
      hasUser,
      isSelectingProxy,
      setIsSelectingProxy,
      isInitalized,
      setInitalized,
      isLoading,
      setLoading,
      setHasLoggedOut,
      setUser,
      getUser,
    }
  },
  {
    persist: { pick: ['proxyOfUser', 'isSelectingProxy'], storage: localStorage },
  },
)

export type AAuthUser = ANavigationProfileInterface & { first_name: string; last_name: string }
